import { gql } from '@apollo/client';

export const ASSESSMENTS = gql`
  query assessments($workspaceId: UxId) {
    assessments(workspaceId: $workspaceId) {
      id
      name
      description
      AssessmentLiveRisk {
        expectedLoss
      }
      startedDate
      reviewDate
      scenarios {
        id
        isIncomplete
        name
        ownerName
        liveRisk {
          expectedLoss
        }
        source
        event
        consequence
        riskIdentifiedDate
        editedAt
        status
        meta {
          createdAt
          updatedAt
        }
        assessment {
          id
        }
      }
    }
  }
`;

export const INCIDENTS = gql`
  query incidents($workspaceId: UxId) {
    incidents(workspaceId: $workspaceId) {
      id
      name
      ref
      source
      incidentType
      dateOfIncident
      description
      financialImpactLower
      financialImpactUpper
      impact
      severity
      owner {
        id
        text
        email
        inviteId
        memberId
        profilePhotoPath
      }
      scenarios {
        id
        name
        assessment {
          id
          name
        }
        source
        event
        consequence
        liveRisk {
          expectedLoss
        }
        ownerName
        riskIdentifiedDate
        isIncomplete
        editedAt
        status
        meta {
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const INCIDENT = gql`
  query incident($id: UxId!, $versionId: UxId) {
    incident(id: $id, versionId: $versionId) {
      id
      name
      ref
      source
      incidentType
      dateOfIncident
      description
      financialImpactLower
      financialImpactUpper
      impact
      severity
      owner {
        id
        text
        email
        inviteId
        memberId
        profilePhotoPath
      }
      scenarios {
        id
        name
        assessment {
          id
          name
        }
        source
        event
        consequence
        liveRisk {
          expectedLoss
        }
        ownerName
        riskIdentifiedDate
        isIncomplete
        status
        riskManagementPlans {
          id
        }
        meta {
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const SCENARIO_TEMPLATES = gql`
  query scenarioTemplates {
    scenarioTemplates {
      id
      name
      author
      groupName
      description
      source
      event
      consequence
      riskOwner
      riskNarrative
      expectedLoss
      frequencyTimes
      frequencyYears
      frequencyNote
      managementStrategy
      lowerFormula
      upperFormula
      note
    }
  }
`;

export const SCENARIO_GROUPS = gql`
  query scenarioGroups($workspaceId: UxId) {
    scenarioGroups(workspaceId: $workspaceId) {
      id
      name
      description
      author
      authorLogoPath
      category
      isSystem
      scenarios {
        id
      }
    }
  }
`;

export const SCENARIO_GROUPS_WITH_SCENARIOS = gql`
  query scenarioGroupsWithScenarios($workspaceId: UxId) {
    scenarioGroups(workspaceId: $workspaceId) {
      id
      name
      description
      author
      authorLogoPath
      category
      isSystem
      scenarios {
        id
        name
        description
        ownerName
        source
        event
        consequence
        riskNarrative
        frequencyNote
        managementStrategy
        managementNote
        consequenceNote
        lowerFormula
        upperFormula
        assessment {
          id
        }
        baseRisk {
          frequencyTimes
          frequencyYears
          expectedLoss
        }
        projectedRisk {
          frequencyTimes
          frequencyYears
          expectedLoss
        }
        liveRisk {
          frequencyTimes
          frequencyYears
          expectedLoss
        }
      }
    }
  }
`;

export const CONTROLS = gql`
  query controls($workspaceId: UxId, $selection: ControlSelectionInput) {
    controls(workspaceId: $workspaceId, selection: $selection) {
      id
      name
      shortname
      description
      owner {
        id
        text
        email
        inviteId
        memberId
        profilePhotoPath
      }
      annualCost
      vendor
      strategy
      status
      origin
      linkUrl
      linkText
      expectedActiveDate
      actualActiveDate
      reviewDate
      existingMaturity
      targetMaturity
      frameworkControls {
        id
        name
        shortname
        control_identifier
        strategy
        controlFramework {
          id
          name
          shortname
        }
      }
      riskManagementPlansCount
      meta {
        createdAt
        updatedAt
      }
    }
  }
`;

export const CONTROL_RMP = gql`
  query controlRmp($id: UxId!) {
    control(id: $id) {
      id
      strategy
      status
      riskManagementPlans {
        id
        startingFrequency
        startingLower
        startingUpper
        modifiers {
          id
          type
          consequenceUpper
          consequenceLower
          frequencyReduction
          isPercentage
          totalPolicyCover
          excess
          note
        }
        recommendedModifiers {
          type
          data {
            upper
            lower
            value
            isPercentage
          }
        }
        scenario {
          id
          name
          source
          event
          consequence
          ownerName
          isIncomplete
          assessment {
            id
            name
          }
          baseRisk {
            upper
            lower
            frequencyTimes
            frequencyYears
          }
          riskIdentifiedDate
          editedAt
          liveRisk {
            expectedLoss
          }
          status

          meta {
            createdAt
            updatedAt
          }
        }
      }
    }
  }
`;

export const CONTROLS_RMP = gql`
  query controlsRmp($workspaceId: UxId!) {
    controls(workspaceId: $workspaceId) {
      id
      frameworkControls {
        id
        control_identifier
      }
      riskManagementPlans {
        id
        scenario {
          id
        }
      }
    }
  }
`;

export const SCENARIO_RMP = gql`
  query scenarioRmp($id: UxId!, $versionId: UxId) {
    scenario(id: $id, versionId: $versionId) {
      id
      baseRisk {
        upper
        lower
        frequencyTimes
        frequencyYears
      }
      riskManagementPlans {
        id
        startingFrequency
        startingLower
        startingUpper
        control {
          id
          name
          strategy
          ownerName
          annualCost
          status
          existingMaturity
          targetMaturity
          linkUrl
          linkText
          frameworkControls {
            id
            name
            shortname
            control_identifier
            strategy
            controlFramework {
              id
              name
              shortname
            }
          }
          meta {
            createdAt
            updatedAt
          }
        }
        modifiers {
          id
          type
          consequenceUpper
          consequenceLower
          frequencyReduction
          isPercentage
          totalPolicyCover
          excess
          note
        }
        recommendedModifiers {
          type
          data {
            upper
            lower
            value
            isPercentage
          }
        }
      }
    }
  }
`;

export const CONTROL_FRAMEWORKS_LIST = gql`
  query controlFrameworksList {
    controlFrameworks {
      id
      name
      shortname
      controlCount
      author
      frameworkControls {
        id
        shortname
        control_identifier
      }
    }
  }
`;

export const CONTROL_FRAMEWORK_DETAILS = gql`
  query controlFrameworkDetails($id: UxId) {
    controlFrameworks(id: $id) {
      id
      name
      shortname
      controlCount
      author
      description
      frameworkControls {
        id
        name
        shortname
        description
        control_identifier
        strategy
        controlFramework {
          id
          name
          shortname
          author
          framework_identifier
          externalId
          description
        }
      }
    }
  }
`;

export const FRAMEWORK_CONTROL = gql`
  query frameworkControl($id: UxId!) {
    frameworkControl(id: $id) {
      id
      name
      shortname
      description
      control_identifier
      strategy
      controlFramework {
        id
        name
        shortname
        author
        framework_identifier
        externalId
      }
    }
  }
`;

export const INSIGHTS = gql`
  query insights($id: UxId!) {
    assessment(id: $id) {
      id
      name
      AssessmentLiveRisk {
        expectedLoss
      }
      scenarios {
        id
        name
        liveRisk {
          expectedLoss
        }
        incidents {
          id
          financialImpactLower
          financialImpactUpper
          dateOfIncident
          incidentType
        }
        reviewDate
        riskManagementPlans {
          id
          control {
            id
            name
            status
            existingMaturity
            targetMaturity
          }
        }
      }
    }
  }
`;

export const ASSESSMENT = gql`
  query assessment($id: UxId!, $versionId: UxId) {
    assessment(id: $id, versionId: $versionId) {
      id
      name
      description
      executiveSummary
      riskTolerance
      AssessmentBaseRisk {
        expectedLoss
        chart {
          x
          y
        }
      }
      AssessmentProjectedRisk {
        expectedLoss
        chart {
          x
          y
        }
      }
      AssessmentLiveRisk {
        expectedLoss
        chart {
          x
          y
        }
      }
      startedDate
      reviewDate
      variables {
        id
        name
        value
        isInUse
        currency
        isGlobal
        alias
        workspaceVariable
      }
      scenarios {
        id
        name
        source
        event
        consequence
        ownerName
        riskOwner {
          text
        }
        riskIdentifiedDate
        reviewDate
        editedAt
        isIncomplete
        riskNarrative
        frequencyNote
        consequenceNote
        lowerFormula
        upperFormula
        baseRisk {
          expectedLoss
          frequencyTimes
          frequencyYears
          lower
          upper
        }
        projectedRisk {
          expectedLoss
          frequencyTimes
          frequencyYears
          lower
          upper
        }
        liveRisk {
          expectedLoss
          frequencyTimes
          frequencyYears
          lower
          upper
        }
        managementStrategy
        managementNote
        ownerName
        riskManagementPlans {
          id
          control {
            id
            status
            frameworkControls {
              id
              control_identifier
            }
          }
          modifiers {
            id
            type
          }
        }
        status
        meta {
          createdAt
          updatedAt
        }
      }
      versions {
        id
        name
        editable
        legacyVersion
        meta {
          createdBy
          createdAt
        }
      }
    }
  }
`;

export const CONTROL = gql`
  query control($id: UxId!, $versionId: UxId) {
    control(id: $id, versionId: $versionId) {
      id
      name
      shortname
      description
      owner {
        id
        text
        email
        inviteId
        memberId
        profilePhotoPath
      }
      annualCost
      vendor
      strategy
      status
      origin
      linkUrl
      linkText
      expectedActiveDate
      actualActiveDate
      reviewDate
      frameworkControls {
        id
        control_identifier
        controlFramework {
          id
          shortname
        }
        compatibleControls {
          id
          name
          shortname
          control_identifier
          description
          strategy
          controlFramework {
            id
            name
            shortname
            description
            author
            framework_identifier
            externalId
          }
        }
      }
      existingMaturity
      targetMaturity
      riskManagementPlans {
        id
        startingFrequency
        startingLower
        startingUpper
        control {
          id
          status
          strategy
        }
        scenario {
          id
          name
          source
          event
          consequence
          ownerName
          riskIdentifiedDate
          reviewDate
          editedAt
          isIncomplete
          riskNarrative
          frequencyNote
          consequenceNote
          baseRisk {
            expectedLoss
            frequencyTimes
            frequencyYears
            lower
            upper
          }
          projectedRisk {
            expectedLoss
            frequencyTimes
            frequencyYears
            lower
            upper
          }
          liveRisk {
            expectedLoss
            frequencyTimes
            frequencyYears
            lower
            upper
          }
          managementStrategy
          managementNote
          ownerName
          assessment {
            id
            name
          }
          riskManagementPlans {
            id
          }
          status
          meta {
            createdAt
            updatedAt
          }
        }
        modifiers {
          id
          type
          consequenceUpper
          consequenceLower
          frequencyReduction
          isPercentage
          totalPolicyCover
          excess
          note
        }
        recommendedModifiers {
          type
          data {
            upper
            lower
            value
            isPercentage
          }
        }
      }
      meta {
        createdAt
        updatedAt
      }
    }
  }
`;

export const SCENARIO = gql`
  query scenario($id: UxId!, $versionId: UxId) {
    scenario(id: $id, versionId: $versionId) {
      id
      name
      managementNote
      managementStrategy
      source
      event
      consequence
      riskIdentifiedDate
      riskNarrative
      frequencyNote
      consequenceNote
      isIncomplete
      status
      reviewDate
      groupName
      lowerFormula
      upperFormula
      assessment {
        id
        name
      }
      riskOwner {
        text
        email
        id
        profilePhotoPath
      }
      baseRisk {
        expectedLoss
        frequencyTimes
        frequencyYears
        upper
        lower
        chart {
          x
          y
        }
      }
      projectedRisk {
        expectedLoss
        frequencyTimes
        frequencyYears
        lower
        upper
        chart {
          x
          y
        }
      }
      liveRisk {
        expectedLoss
        frequencyTimes
        frequencyYears
        lower
        upper
        lowerThanMinimum
        chart {
          x
          y
        }
      }
      incidents {
        id
        name
        ref
        source
        incidentType
        dateOfIncident
        description
        financialImpactLower
        financialImpactUpper
        impact
        severity
        owner {
          id
          text
          email
          inviteId
          memberId
          profilePhotoPath
        }
        scenarios {
          id
        }
      }
      riskManagementPlans {
        id
        startingFrequency
        startingLower
        startingUpper
        control {
          id
          name
          status
          strategy
          ownerName
          annualCost
          vendor
          origin
          linkUrl
          linkText
          existingMaturity
          targetMaturity
          frameworkControls {
            id
            name
            shortname
            control_identifier
            strategy
            controlFramework {
              id
              name
            }
          }
          meta {
            createdAt
            updatedAt
          }
        }
        modifiers {
          id
          type
          consequenceUpper
          consequenceLower
          frequencyReduction
          isPercentage
          totalPolicyCover
          excess
          note
        }
        recommendedModifiers {
          type
          data {
            upper
            lower
            value
            isPercentage
          }
        }
      }
      editedAt
      meta {
        updatedAt
      }
    }
  }
`;

export const DASHBOARD = gql`
  query dashboard($assessmentId: UxId!) {
    assessment(id: $assessmentId) {
      id
      name
      description
      startedDate
      editedAt
      scenarios {
        id
        isIncomplete
        riskManagementPlans {
          id
          control {
            status
          }
          modifiers {
            id
          }
        }
        incidents {
          id
        }
      }
      meta {
        updatedAt
      }
    }
    dashboard(assessmentId: $assessmentId) {
      expectedLoss {
        unmodifiedRisk
        liveRisk
        projectedRisk
      }
      securityProgram {
        riskReduction
        cost
        savings
      }
      futureSecurityProgram {
        furtherReduction
        additionalCost
        totalSavings
      }
      returnOnInvestment {
        currentProgramme
        futureProgramme
      }
      riskyScenarios {
        scenarios {
          id
          name
          liveRisk {
            expectedLoss
          }
          assessment {
            id
          }
        }
      }
      controlImplementation {
        notStartedCount
        notStartedCost
        inProgressCount
        inProgressCost
        liveCount
        liveCost
        otherCount
        otherCost
        controlsWithoutCost
      }
      overdueReviews {
        assessments
        scenarios
        controls
      }
      averageIncidentFrequency {
        meantimeBetweenIncidents
        meantimeBetweenNearMisses
      }
      riskAvoidance {
        nearMisses
        days
        estimatedLossLower
        estimatedLossUpper
      }
      missingData {
        scenariosWithoutCalculationData
        scenariosWithoutLinkedControls
        scenariosWithoutModifiers
      }
    }
  }
`;

export const VERSIONS = gql`
  query versions($assessmentId: UxId!) {
    assessment(id: $assessmentId) {
      versions {
        id
        name
        editable
        legacyVersion
        meta {
          createdBy
          createdAt
        }
      }
    }
  }
`;

// export const TIMELINE = gql`
//   query timeline($workspaceId: UxId!, $filters: TimelineFilterInput) {
//     timeline(workspaceId: $workspaceId, filters: $filters) {
//       total
//       today
//       filters {
//         events
//         owners
//         objects
//       }
//       data {
//         index
//         event_date
//         events {
//           type
//           data
//         }
//       }
//     }
//   }
// `;

const EVENT_FIELDS = gql`
  fragment EventFields on Event {
    id
    objectType
    objectId
    eventType
    notes
    assignedTo {
      id
      text
      email
      inviteId
      memberId
      profilePhotoPath
    }
    dueDate
    completionStatus
    completedBy {
      id
      text
      email
      inviteId
      memberId
      profilePhotoPath
    }
    completedAt
    isOverdue
    meta {
      createdAt
      updatedAt
    }
  }
`;

export const EVENTS = gql`
  ${EVENT_FIELDS}
  query events(
    $workspaceId: UxId!
    $objectType: ObjectKind
    $objectId: UxId
    $eventType: EventKind
    $completionStatus: EventCompletionStatus
    $onlyOpen: Boolean = false
  ) {
    events(
      workspaceId: $workspaceId
      objectType: $objectType
      objectId: $objectId
      eventType: $eventType
      completionStatus: $completionStatus
      onlyOpen: $onlyOpen
    ) {
      ...EventFields
    }
  }
`;

export const EVENT = gql`
  ${EVENT_FIELDS}
  query event($id: UxId!) {
    event(id: $id) {
      ...EventFields
    }
  }
`;

const COMPARISON_ASSESSMENT_FRAGMENT = gql`
  fragment ComparisonAssessmentData on Assessment {
    id
    name
    riskTolerance
    reviewDate
    AssessmentBaseRisk {
      expectedLoss
      chart {
        x
        y
      }
    }
    AssessmentProjectedRisk {
      expectedLoss
      chart {
        x
        y
      }
    }
    AssessmentLiveRisk {
      expectedLoss
      chart {
        x
        y
      }
    }
    scenarios {
      id
      name
      source
      event
      consequence
      ownerName
      riskOwner {
        text
      }
      referenceId
      isIncomplete
      lowerFormula
      upperFormula
      baseRisk {
        expectedLoss
        frequencyTimes
        frequencyYears
        lower
        upper
      }
      projectedRisk {
        expectedLoss
        frequencyTimes
        frequencyYears
        lower
        upper
      }
      liveRisk {
        expectedLoss
        frequencyTimes
        frequencyYears
        lower
        upper
        lowerThanMinimum
      }
      riskManagementPlans {
        id
        control {
          id
          status
          frameworkControls {
            id
            control_identifier
          }
        }
        modifiers {
          id
          type
        }
      }
      managementStrategy
      managementNote
      riskIdentifiedDate
      reviewDate
      editedAt
      riskNarrative
      frequencyNote
      consequenceNote
      status
      meta {
        createdAt
        updatedAt
      }
    }
    versions {
      id
      name
      editable
      legacyVersion
      meta {
        createdBy
        createdAt
      }
    }
  }
`;

export const COMPARISON_ASSESSMENT = gql`
  ${COMPARISON_ASSESSMENT_FRAGMENT}
  query comparisonAssessment($assessmentAId: UxId!, $assessmentBId: UxId!, $versionAId: UxId, $versionBId: UxId) {
    assessmentA: assessment(id: $assessmentAId, versionId: $versionAId) {
      ...ComparisonAssessmentData
    }
    assessmentB: assessment(id: $assessmentBId, versionId: $versionBId) {
      ...ComparisonAssessmentData
    }
  }
`;

const COMPARISON_SCENARIO_FRAGMENT = gql`
  fragment ComparisonScenarioData on Scenario {
    id
    name
    source
    event
    consequence
    riskOwner {
      text
      email
      id
      profilePhotoPath
    }
    riskNarrative
    frequencyNote
    consequenceNote
    managementStrategy
    managementNote
    status
    reviewDate
    riskIdentifiedDate
    editedAt
    baseRisk {
      expectedLoss
      frequencyTimes
      frequencyYears
      lower
      upper
      chart {
        x
        y
      }
    }
    projectedRisk {
      expectedLoss
      frequencyTimes
      frequencyYears
      lower
      upper
      chart {
        x
        y
      }
    }
    liveRisk {
      expectedLoss
      frequencyTimes
      frequencyYears
      lower
      upper
      lowerThanMinimum
      chart {
        x
        y
      }
    }
    assessment {
      id
      name
      versions {
        id
        name
        editable
        legacyVersion
        meta {
          createdBy
          createdAt
        }
      }
    }
  }
`;

export const COMPARE_SCENARIOS = gql`
  ${COMPARISON_SCENARIO_FRAGMENT}
  query compareScenarios($scenarioAId: UxId!, $scenarioBId: UxId!, $versionAId: UxId, $versionBId: UxId) {
    scenarioA: scenario(id: $scenarioAId, versionId: $versionAId) {
      ...ComparisonScenarioData
    }
    scenarioB: scenario(id: $scenarioBId, versionId: $versionBId) {
      ...ComparisonScenarioData
    }
  }
`;

export const SCENARIO_CONTROLS = gql`
  query scenarioControls($id: UxId!, $versionId: UxId) {
    scenario(id: $id, versionId: $versionId) {
      id
      name
      baseRisk {
        upper
        lower
        frequencyTimes
        frequencyYears
      }
      riskManagementPlans {
        id
        startingFrequency
        startingLower
        startingUpper
        order
        control {
          id
          name
          shortname
          description
          strategy
          status
          origin
          annualCost
          vendor
          linkUrl
          linkText
          expectedActiveDate
          actualActiveDate
          reviewDate
          existingMaturity
          targetMaturity
          owner {
            id
            text
            email
            memberId
            inviteId
            profilePhotoPath
          }
          frameworkControls {
            id
            name
            shortname
            control_identifier
            strategy
            controlFramework {
              id
              name
              shortname
            }
          }
          meta {
            createdAt
            updatedAt
          }
        }
        modifiers {
          id
          type
          consequenceUpper
          consequenceLower
          frequencyReduction
          isPercentage
          totalPolicyCover
          excess
          note
        }
        recommendedModifiers {
          type
          data {
            upper
            lower
            value
            isPercentage
          }
        }
      }
    }
  }
`;

export const FILTERS_FOR = gql`
  query filtersFor($workspaceId: UxId!, $object: FilterObjectKey!, $includeCounts: Boolean = false) {
    filtersFor(workspaceId: $workspaceId, object: $object, includeCounts: $includeCounts) {
      controls {
        strategy {
          unifiedKey
          label
          enabled
        }
        status {
          unifiedKey
          label
          enabled
        }
        owners {
          unifiedKey
          label
          enabled
          email
        }
        frameworks {
          unifiedKey
          label
          enabled
        }
        assessments {
          unifiedKey
          label
          enabled
        }
        existingMaturity {
          unifiedKey
          label
          enabled
        }
        targetMaturity {
          unifiedKey
          label
          enabled
        }
      }
    }
  }
`;

export const COMPLIANCE_FRAMEWORK_GAUGE = gql`
  query complianceFrameworkGauge($id: UxId!, $workspaceId: UxId!) {
    complianceFrameworks(id: $id, workspaceId: $workspaceId) {
      controlsCount
      controlsImplementedCount
      controlsPlannedCount
    }
  }
`;

export const COMPLIANCE_FRAMEWORK = gql`
  query complianceFramework($id: UxId!, $workspaceId: UxId!) {
    complianceFrameworks(id: $id, workspaceId: $workspaceId) {
      controlsCount
      controlsImplementedCount
      controlsPlannedCount
      controlFramework {
        id
        shortname
        name
        description
        author
      }
      complianceControls {
        complianceStatus
        frameworkControl {
          id
          control_identifier
          name
          description
          strategy
          controlFramework {
            shortname
            author
          }
        }
        compliantRmpControls {
          id
          name
          strategy
          status
          owner {
            id
            text
            email
            memberId
            inviteId
            profilePhotoPath
          }
          frameworkControls {
            controlFramework {
              id
              shortname
            }
          }
        }
      }
    }
  }
`;
